/**
 * Limpieza local de sesión. Se usa cuando signOut() no pudo cerrar sesión
 * (p. ej. falla el POST /logout por red: supabase-js devuelve el error y NO
 * borra la sesión local, ver GoTrueClient._signOut), para que un equipo
 * compartido no quede con la sesión abierta.
 */
type ReadWriteStorage = Pick<Storage, 'getItem' | 'removeItem' | 'key' | 'length'>;

// Claves que supabase-js guarda: sb-<ref>-auth-token (+ -code-verifier, -user).
const SUPABASE_SESSION_KEY = /^sb-.+-auth-token(-code-verifier|-user)?$/;

// Metadata de sesión/UI de la app que no debe sobrevivir a un logout (L-4, SEC).
export const SESSION_METADATA_KEYS = [
  'quipus_company',
  'quipus_filter_periodo',
  'quipus_active_user',
  'quipus_login_audited_uid',
  'quipus_last_activity',
] as const;

/** Borra la sesión de supabase-js del navegador (sin llamar al servidor). */
export function clearSupabaseSession(storage?: ReadWriteStorage): void {
  try {
    // Se resuelve dentro del try: acceder a localStorage puede lanzar (SecurityError).
    const store = storage ?? localStorage;
    const keys: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && SUPABASE_SESSION_KEY.test(k)) keys.push(k);
    }
    for (const k of keys) store.removeItem(k);
  } catch {
    // storage no disponible: no hay nada que limpiar
  }
}

/** Borra la metadata de sesión de la app (nombre/email, empresa activa, marcadores). */
export function clearSessionMetadata(storage?: Pick<Storage, 'removeItem'>): void {
  let store: Pick<Storage, 'removeItem'>;
  try {
    store = storage ?? localStorage;
  } catch {
    return; // localStorage bloqueado: no hay metadata que limpiar
  }
  for (const k of SESSION_METADATA_KEYS) {
    try {
      store.removeItem(k);
    } catch {
      // ignorar: sigue con las demás claves
    }
  }
}
